import React, { useEffect, useState } from 'react';
import {
  Box,
  Divider,
  Input,
  NumericInput,
  Switch,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import {
  AlertInfoIcon,
  MoneyIcon,
} from '@fortress-technology-solutions/fortress-component-library/Icons';
import {
  Grid,
  SingleSelect,
} from '@fortress-technology-solutions/fortress-component-library/Molecules';
import {
  useConfirmModal,
  ModalConfirm,
} from '@fortress-technology-solutions/fortress-component-library/Molecules_Fortress';
import { FormattedMessage } from 'react-intl';
import { formatCurrency, removeNonNumericCharacters } from '../../../../utils';
import generalMessages from '../../../App/messages';
import componentMessages from './messages';
import ConfirmationModal from './components/ConfirmationModal';
import CollectionsModal from './CollectionsModal';
import SetupGridItem from '../../SetupGridItem';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  COLLECTIONS_DEFAULT_DAYS_AFTER_FAS,
  COLLECTIONS_DEFAULT_MINIMUM_AMOUNT,
} from '../../../../constants/propertyCollectionSettings';

const messages = { ...generalMessages, ...componentMessages };

const initialCollectionSetting = {
  enabled: false,
  minimumAmount: COLLECTIONS_DEFAULT_MINIMUM_AMOUNT,
  daysAfterFAS: COLLECTIONS_DEFAULT_DAYS_AFTER_FAS,
  collectionAgency: null,
};

const createAgencyData = (orgAgency) => ({
  id: orgAgency.collectionAgency.id,
  name: orgAgency.collectionAgency.name,
  enabledFTP: orgAgency.collectionAgency.enabledFTP,
  enabledAPI: orgAgency.collectionAgency.enabledAPI,
  enabled: orgAgency.collectionAgency.enabled,
  createdAt: orgAgency.collectionAgency.createdAt,
  updatedAt: orgAgency.collectionAgency.updatedAt,
  deletedAt: orgAgency.collectionAgency.deletedAt,
  organizationCollecti: orgAgency.createdAt || new Date().toISOString(),
});

export const CollectionsSetupSection = (props) => {
  const { intl, propertyDetails, editMode, onChange, onSwitchChange } = props;

  const { multipleCollectionAgencies } = useFlags();

  const organizationCollectionAgencies =
    propertyDetails?.organizationCollectionAgencies || [];

  const getCollectionSettingsFromOrgAgencies = () => {
    const propertyCollectionSetting =
      propertyDetails?.propertyCollectionSetting;

    if (!propertyCollectionSetting || !propertyCollectionSetting.enabled) {
      return initialCollectionSetting;
    }

    return {
      enabled: propertyCollectionSetting.enabled,
      minimumAmount:
        propertyCollectionSetting.minimumAmount ||
        COLLECTIONS_DEFAULT_MINIMUM_AMOUNT,
      daysAfterFAS:
        propertyCollectionSetting.daysAfterFAS ||
        COLLECTIONS_DEFAULT_DAYS_AFTER_FAS,
      collectionAgency: propertyCollectionSetting.collectionAgency
        ? {
            id: propertyCollectionSetting.collectionAgency.id,
            name: propertyCollectionSetting.collectionAgency.name,
          }
        : null,
    };
  };

  const [collectionSettings, setCollectionSettings] = useState(() => {
    return getCollectionSettingsFromOrgAgencies();
  });

  const [showModal, setShowModal] = useState(false);
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);
  const [modalValues, setModalValues] = useState({
    agency: null,
    amount: null,
  });

  const { closeConfirmModal, openConfirmModal, openConfirm } =
    useConfirmModal(false);

  useEffect(() => {
    let newSettings = getCollectionSettingsFromOrgAgencies();

    // Auto-selection logic when FF is ON and Collection is Enabled
    if (
      multipleCollectionAgencies &&
      organizationCollectionAgencies.length > 0 &&
      newSettings.enabled
    ) {
      const currentAgencyId = newSettings.collectionAgency?.id;

      // only auto-select an agency when none is set. Do NOT overwrite an
      // already-stored agency (e.g. the legacy "manual" value) — that silently staged
      // a manual -> Hunter Warfield change on page load and corrupted the agency on save.
      if (!currentAgencyId) {
        const enabledAgency = organizationCollectionAgencies.find(
          (oca) => !!oca.apiKey && oca.collectionAgency?.id,
        );

        if (enabledAgency) {
          const agencyData = createAgencyData(enabledAgency);

          newSettings = {
            ...newSettings,
            collectionAgency: agencyData,
          };

          onChange({
            target: {
              name: 'propertyCollectionSetting',
              value: newSettings,
            },
          });
        }
      }
    }

    setCollectionSettings(newSettings);
  }, [
    propertyDetails?.propertyCollectionSetting,
    multipleCollectionAgencies,
    organizationCollectionAgencies,
    onChange,
  ]);

  const handleSwitchChange = (event) => {
    const value = event.target.checked;

    if (value === true) {
      if (multipleCollectionAgencies) {
        setModalValues({
          agency: collectionSettings.collectionAgency,
          amount: collectionSettings.minimumAmount,
        });
        setShowModal(true);
      } else {
        openConfirmModal();
      }
      return;
    }
    // Disabling collections must be explicit: open a confirmation instead of
    // silently committing enabled=false. This prevents an accidental toggle (or
    // a canceled enable flow) from persisting OFF on the next form save.
    setShowDisableConfirm(true);
  };

  const confirmDisableCollections = () => {
    setCollectionSettings({ ...collectionSettings, enabled: false });
    onSwitchChange({
      target: {
        name: 'propertyCollectionSetting.enabled',
        checked: false,
      },
    });
    setShowDisableConfirm(false);
  };

  const cancelDisableCollections = () => {
    // Keep collections enabled; the controlled toggle stays ON.
    setShowDisableConfirm(false);
  };

  const confirmAutomatedCollections = () => {
    if (multipleCollectionAgencies) {
      const finalSettings = {
        ...collectionSettings,
        enabled: true,
        ...(modalValues.agency && { collectionAgency: modalValues.agency }),
        ...(modalValues.amount !== null && {
          minimumAmount: modalValues.amount,
        }),
      };

      setCollectionSettings(finalSettings);

      onSwitchChange({
        target: {
          name: 'propertyCollectionSetting.enabled',
          checked: true,
        },
      });

      onChange({
        target: {
          name: 'propertyCollectionSetting',
          value: finalSettings,
        },
      });

      setShowModal(false);
    } else {
      setCollectionSettings({ ...collectionSettings, enabled: true });
      onSwitchChange({
        target: {
          name: 'propertyCollectionSetting.enabled',
          checked: true,
        },
      });
    }
  };

  const closeModal = () => {
    setModalValues({
      agency: null,
      amount: null,
    });
    setShowModal(false);
  };

  const handleNumericFieldChange = (event) => {
    const { name, value } = event.target;
    const fieldValue = removeNonNumericCharacters(value);
    const updatedSettings = {
      ...collectionSettings,
      [name.split('.')[1]]: fieldValue,
    };

    setCollectionSettings(updatedSettings);
    onChange({
      target: {
        name,
        value: fieldValue,
      },
    });
  };

  const handleCollectionsAgencyChange = (selectedAgencyId) => {
    const selectedOrgAgency = organizationCollectionAgencies.find(
      (oca) => oca.collectionAgencyId === selectedAgencyId,
    );

    if (!selectedOrgAgency) {
      return;
    }

    const agencyData = createAgencyData(selectedOrgAgency);

    const updatedSettings = {
      ...collectionSettings,
      collectionAgency: agencyData,
    };

    setCollectionSettings(updatedSettings);
    onChange({
      target: {
        name: 'propertyCollectionSetting',
        value: updatedSettings,
      },
    });
  };

  const handleCollectionsAgencyModalChange = (agencyId) => {
    const selectedOrgAgency = organizationCollectionAgencies.find(
      (oca) => oca.collectionAgencyId === agencyId,
    );

    if (!selectedOrgAgency) {
      setModalValues((prev) => ({ ...prev, agency: null }));
      return;
    }

    const agencyData = createAgencyData(selectedOrgAgency);

    setModalValues((prev) => ({
      ...prev,
      agency: agencyData,
    }));

    if (selectedOrgAgency.propertyCollectionSetting?.minimumAmount) {
      setModalValues((prev) => ({
        ...prev,
        amount: selectedOrgAgency.propertyCollectionSetting.minimumAmount,
      }));
    }
  };

  const handleMinimumAmountModalChange = (amount) => {
    setModalValues((prev) => ({
      ...prev,
      amount: amount,
    }));
  };

  const { enabled, minimumAmount, daysAfterFAS, collectionAgency } =
    collectionSettings;

  const daysBeforeInvalid = daysAfterFAS < 1 || daysAfterFAS > 999;
  const minimumAmountInvalid =
    minimumAmount < COLLECTIONS_DEFAULT_MINIMUM_AMOUNT || minimumAmount > 999;

  let collectionsAgencyOptions = [];

  if (multipleCollectionAgencies) {
    const availableAgencies = [
      { name: 'Hunter Warfield', searchKey: 'Hunter Warfield' },
      { name: 'Rent Debt', searchKey: 'Rent Debt' },
    ];

    collectionsAgencyOptions = availableAgencies.map((agency) => {
      const orgAgency = organizationCollectionAgencies.find((oca) =>
        oca.collectionAgency?.name?.includes(agency.searchKey),
      );

      const hasApiKey = orgAgency?.apiKey;
      const isDisabled = !hasApiKey;

      let tooltipMessage = null;
      if (isDisabled && messages.collectionsAgencyTooltip) {
        tooltipMessage = intl.formatMessage(messages.collectionsAgencyTooltip);
      }

      return {
        value:
          orgAgency?.collectionAgency?.id ||
          `${agency.searchKey.toLowerCase().replace(' ', '-')}-id`,
        text: agency.name,
        disabled: isDisabled,
        tooltip: isDisabled ? tooltipMessage : undefined,
      };
    });

    // surface a stored agency not in the list (e.g. "manual") as a disabled
    // option, so it displays truthfully instead of falling back to the first option.
    if (
      collectionAgency?.id &&
      !collectionsAgencyOptions.some((opt) => opt.value === collectionAgency.id)
    ) {
      collectionsAgencyOptions = [
        {
          value: collectionAgency.id,
          text: collectionAgency.name,
          disabled: true,
        },
        ...collectionsAgencyOptions,
      ];
    }
  }

  return (
    <>
      <Grid item xs={12}>
        <Divider sx={{ marginY: 2 }} />
        <Typography variant="titleLarge">
          <FormattedMessage {...messages.collections} />
        </Typography>
      </Grid>

      <SetupGridItem
        TooltipProps={{
          title: <FormattedMessage {...messages.collectionsTooltip} />,
          children: <AlertInfoIcon />,
        }}
        label={<FormattedMessage {...messages.collections} />}
      >
        {editMode ? (
          <Switch
            name="propertyCollectionSetting.enabled"
            id="collections-setting-switch"
            disabled={!editMode}
            onChange={handleSwitchChange}
            checked={enabled}
            inputProps={{ 'data-checked': enabled }}
          />
        ) : (
          <Typography color={enabled ? 'primary' : 'action.disabled'}>
            {enabled ? (
              <FormattedMessage {...messages.on} />
            ) : (
              <FormattedMessage {...messages.off} />
            )}
          </Typography>
        )}
      </SetupGridItem>

      <SetupGridItem label={<FormattedMessage {...messages.daysAfterFAS} />}>
        <NumericInput
          name="propertyCollectionSetting.daysAfterFAS"
          id="collection-settings-days-after-fas"
          value={daysAfterFAS}
          onChange={handleNumericFieldChange}
          error={daysBeforeInvalid}
          readOnly={!editMode}
          helperText={
            daysBeforeInvalid && (
              <FormattedMessage {...messages.daysAfterFASError} />
            )
          }
        />
      </SetupGridItem>

      {multipleCollectionAgencies &&
        enabled &&
        collectionsAgencyOptions.length > 0 && (
          <SetupGridItem
            label={<FormattedMessage {...messages.collectionsAgency} />}
          >
            {editMode ? (
              <SingleSelect
                sx={{ width: 'calc(100% - 32px)' }}
                options={collectionsAgencyOptions}
                value={collectionAgency?.id || ''}
                onChange={handleCollectionsAgencyChange}
              />
            ) : (
              <Typography>{collectionAgency?.name || '---'}</Typography>
            )}
          </SetupGridItem>
        )}

      {enabled && (
        <SetupGridItem label={<FormattedMessage {...messages.minAmount} />}>
          {editMode ? (
            multipleCollectionAgencies ? (
              <NumericInput
                name="propertyCollectionSetting.minimumAmount"
                id="collection-settings-minimum-amount"
                value={minimumAmount}
                onChange={handleNumericFieldChange}
                required
                error={minimumAmountInvalid}
                readOnly={!editMode}
                helperText={
                  minimumAmountInvalid && (
                    <FormattedMessage {...messages.minAmountError} />
                  )
                }
                startAdornment={<MoneyIcon />}
                type="number"
              />
            ) : (
              <Input
                name="propertyCollectionSetting.minimumAmount"
                id="collection-settings-minimum-amount"
                value={minimumAmount}
                onChange={handleNumericFieldChange}
                required
                type="number"
                startAdornment={<MoneyIcon />}
                error={minimumAmountInvalid}
                helperText={
                  minimumAmountInvalid && (
                    <Typography variant="bodyMedium" color="error">
                      <FormattedMessage {...messages.minAmountError} />
                    </Typography>
                  )
                }
              />
            )
          ) : (
            <Typography>{formatCurrency(intl, minimumAmount)}</Typography>
          )}
        </SetupGridItem>
      )}

      {multipleCollectionAgencies ? (
        <CollectionsModal
          isOpen={showModal}
          closeModal={closeModal}
          confirm={confirmAutomatedCollections}
          propertyName={propertyDetails?.name}
          collectionsAgency={collectionAgency?.id || ''}
          minimumAmount={minimumAmount}
          collectionsAgencyOptions={collectionsAgencyOptions}
          onCollectionsAgencyChange={handleCollectionsAgencyModalChange}
          onMinimumAmountChange={handleMinimumAmountModalChange}
          minimumCollectionsAmount={COLLECTIONS_DEFAULT_MINIMUM_AMOUNT}
        />
      ) : (
        <ConfirmationModal
          isOpen={openConfirm}
          closeModal={closeConfirmModal}
          confirm={confirmAutomatedCollections}
          propertyName={propertyDetails?.name}
        />
      )}

      <ModalConfirm
        open={showDisableConfirm}
        onClose={cancelDisableCollections}
        title={intl.formatMessage(messages.disableConfirmTitle)}
        actionsProps={[
          {
            variant: 'text',
            onClick: cancelDisableCollections,
            children: <FormattedMessage {...messages.modalCancel} />,
          },
          {
            variant: 'primary',
            onClick: confirmDisableCollections,
            children: <FormattedMessage {...messages.disableConfirmAction} />,
          },
        ]}
      >
        <Box sx={{ width: 480 }}>
          <Typography variant="bodyMedium">
            <FormattedMessage {...messages.disableConfirmBody} />
          </Typography>
        </Box>
      </ModalConfirm>
    </>
  );
};
